import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex items-start justify-between gap-4 max-[640px]:flex-col max-[640px]:items-stretch" }
const _hoisted_2 = { class: "space-y-1" }
const _hoisted_3 = {
  key: 0,
  class: "space-y-4 p-6"
}
const _hoisted_4 = { class: "grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5" }
const _hoisted_5 = {
  key: 1,
  class: "px-6 py-6"
}
const _hoisted_6 = { class: "rounded-[1.1rem] border border-[#dbe5f2] bg-white p-5" }
const _hoisted_7 = { class: "mb-4 flex items-center justify-between gap-3" }

import { computed, onBeforeUnmount, nextTick, ref, watch, inject } from "vue";
import Chart from "chart.js/auto";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type {
  DashboardSummary,
  DashboardWorkloadItem,
} from "@/types/dashboard";


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkloadByStatusChart',
  props: {
    data: {},
    loading: { type: Boolean },
    role: {},
    summary: {}
  },
  setup(__props: any) {

const props = __props;

const comfortMode = inject<any>("comfortMode", ref("comfortable"));

const canvasRef = ref<HTMLCanvasElement | null>(null);
let chart: Chart | null = null;

const statusCount = computed(() => {
  const counts = new Map<string, number>();

  for (const item of props.data ?? []) {
    counts.set(normalizeStatus(item.status), item.count);
  }

  return counts;
});

const totalTasksCount = computed(() => {
  return props.summary?.total_tasks ?? props.summary?.totalTickets ?? 0;
});

const chartData = computed(() => {
  if (props.role === "client") {
    return [
      {
        label: "Completed",
        value: sumCounts(["completed", "done"]),
        color: "#10b981",
      },
      {
        label: "In Progress",
        value: sumCounts(["ongoing", "in_progress", "for_qa"]),
        color: "#3b82f6",
      },
      {
        label: "Pending",
        value: sumCounts(["not_started", "pending", "hold"]),
        color: "#94a3b8",
      },
    ];
  }

  return [
    {
      label: "Completed",
      value: sumCounts(["completed", "done"]),
      color: "#10b981",
    },
    {
      label: "In Progress",
      value: sumCounts(["ongoing", "in_progress", "for_qa"]),
      color: "#635bff",
    },
    {
      label: "Pending",
      value: sumCounts(["not_started", "pending", "hold"]),
      color: "#ef4444",
    },
  ];
});

const hasData = computed(() => totalTasksCount.value > 0 || chartData.value.some((item) => item.value > 0));

const chartDescription = computed(() =>
  String(props.role || "").toLowerCase() === "client"
    ? "A streamlined view of work progress, completions, and new lead activity."
    : "A streamlined view of work progress, completions, and new lead activity."
);

function normalizeStatus(status: string) {
  return String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");
}

function sumCounts(statuses: string[]) {
  return statuses.reduce(
    (sum, key) => sum + (statusCount.value.get(key) ?? 0),
    0
  );
}

function destroyChart() {
  chart?.destroy();
  chart = null;
}

function renderChart() {
  if (!canvasRef.value || !hasData.value) return;

  destroyChart();

  const labels = chartData.value.map((item) => item.label);
  const values = chartData.value.map((item) => item.value);
  const colors = chartData.value.map((item) => item.color);

  const isComfortable = comfortMode.value === "comfortable";
  const tickFontSize = isComfortable ? 13 : 11;
  const valueFontSize = isComfortable ? 13 : 11;

  chart = new Chart(canvasRef.value, {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: "Tasks",
          data: values,
          backgroundColor: colors,
          borderRadius: 10,
          barPercentage: 0.68,
          categoryPercentage: 0.72,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "rgba(71,85,105,0.95)",
            font: { size: tickFontSize, weight: "600" },
          },
        },
        y: {
          beginAtZero: true,
          suggestedMax: Math.max(...values, 1),
          ticks: {
            color: "rgba(71,85,105,0.85)",
            font: { size: tickFontSize },
          },
          grid: {
            color: "rgba(226,232,240,0.95)",
            borderDash: [4, 4],
          },
        },
      },
    },
    plugins: [
      {
        id: "valueLabels",
        afterDatasetsDraw(currentChart) {
          const { ctx } = currentChart;
          ctx.save();
          ctx.fillStyle = "#0f172a";
          ctx.font = `600 ${valueFontSize}px Inter, ui-sans-serif, system-ui, sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";

          const meta = currentChart.getDatasetMeta(0);
          meta.data.forEach((bar, index) => {
            const value = values[index];
            if (value == null) return;
            ctx.fillText(String(value), bar.x, bar.y - 6);
          });
          ctx.restore();
        },
      },
    ],
  });
}

watch(
  () => [props.data, props.summary, props.loading],
  () => nextTick(renderChart),
  { deep: true, immediate: true }
);

onBeforeUnmount(() => {
  destroyChart();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Card), { class: "flex h-full min-w-0 w-full flex-col overflow-hidden rounded-[1.25rem] border border-[#d8e1f0] bg-white text-[#1f2937] shadow-[0_18px_40px_rgba(15,23,42,0.06)]" }, {
    default: _withCtx(() => [
      _createVNode(_unref(CardHeader), {
        class: _normalizeClass(["border-b border-[#e5ecf5] bg-[#f8fbff]", _unref(comfortMode) === 'comfortable' ? 'px-6 md:px-8 py-5 md:py-6' : 'px-6 py-5'])
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(CardTitle), {
                class: _normalizeClass(
              _unref(cn)(
                'm-0 font-black text-[#0f172a]',
                _unref(comfortMode) === 'comfortable' ? 'text-lg md:text-xl' : 'text-[1.05rem] font-extrabold'
              )
            )
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(__props.role === 'client' ? 'Work Summary' : 'Workload by Status'), 1)
                ]),
                _: 1
              }, 8, ["class"]),
              _createVNode(_unref(CardDescription), {
                class: _normalizeClass(
              _unref(cn)(
                'font-medium mt-1 text-[#64748b]',
                _unref(comfortMode) === 'comfortable' ? 'text-sm md:text-base' : 'text-[0.88rem]'
              )
            )
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(__props.role === 'client' ? 'A quick view of completed, active, and pending work across your services.' : chartDescription.value), 1)
                ]),
                _: 1
              }, 8, ["class"])
            ])
          ])
        ]),
        _: 1
      }, 8, ["class"]),
      _createVNode(_unref(CardContent), { class: "flex-1 p-0" }, {
        default: _withCtx(() => [
          (__props.loading)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (n) => {
                    return _createVNode(_unref(Skeleton), {
                      key: n,
                      class: "h-[4.75rem] rounded-[1rem] bg-[#eef4ff]"
                    })
                  }), 64))
                ]),
                _createVNode(_unref(Skeleton), { class: "h-[20rem] rounded-[1.15rem] bg-[#eef4ff]" })
              ]))
            : (!hasData.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[0] || (_cache[0] = [
                  _createElementVNode("div", { class: "grid min-h-[24rem] place-items-center rounded-[1.15rem] border border-dashed border-[#dbe5f2] bg-[#fafcff] px-6 text-center" }, [
                    _createElementVNode("div", { class: "space-y-2" }, [
                      _createElementVNode("p", { class: "text-[1rem] font-bold text-[#0f172a]" }, " No workload data yet "),
                      _createElementVNode("p", { class: "text-[0.92rem] leading-7 text-[#64748b] font-medium" }, " Workload metrics will appear once task and lead activity is available. ")
                    ])
                  ], -1)
                ]))]))
              : (_openBlock(), _createElementBlock("div", {
                  key: 2,
                  class: _normalizeClass(["p-6 text-left", _unref(comfortMode) === 'comfortable' ? 'space-y-6 md:p-8' : 'space-y-5 p-6'])
                }, [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, [
                      _createElementVNode("p", {
                        class: _normalizeClass(
                _unref(cn)(
                  'font-bold uppercase tracking-[0.14em] text-[#64748b]',
                  _unref(comfortMode) === 'comfortable' ? 'text-xs md:text-sm' : 'text-[0.72rem]'
                )
              )
                      }, " Tasks ", 2),
                      _createElementVNode("p", {
                        class: _normalizeClass(
                _unref(cn)(
                  'font-medium text-[#64748b]',
                  _unref(comfortMode) === 'comfortable' ? 'text-xs md:text-sm' : 'text-[0.78rem]'
                )
              )
                      }, " Status distribution across the current scope ", 2)
                    ]),
                    _createElementVNode("div", {
                      class: _normalizeClass(_unref(comfortMode) === 'comfortable' ? 'h-[25rem]' : 'h-[20rem]')
                    }, [
                      _createElementVNode("canvas", {
                        ref_key: "canvasRef",
                        ref: canvasRef
                      }, null, 512)
                    ], 2)
                  ]),
                  (__props.role === 'client')
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: _normalizeClass(["rounded-2xl border border-blue-100 bg-[#eff6ff]/35", _unref(comfortMode) === 'comfortable' ? 'p-5 md:p-6' : 'p-3.5'])
                      }, [
                        _createElementVNode("p", {
                          class: _normalizeClass(
              _unref(cn)(
                'font-extrabold uppercase tracking-wider text-[#1e40af] mb-1.5',
                _unref(comfortMode) === 'comfortable' ? 'text-xs md:text-sm' : 'text-[0.72rem]'
              )
            )
                        }, " Insight ", 2),
                        _createElementVNode("p", {
                          class: _normalizeClass(
              _unref(cn)(
                'font-semibold text-slate-700 leading-relaxed',
                _unref(comfortMode) === 'comfortable' ? 'text-sm md:text-base' : 'text-[0.82rem]'
              )
            )
                        }, " Most active work is currently focused on onboarding and optimization of your core services. ", 2)
                      ], 2))
                    : _createCommentVNode("", true)
                ], 2))
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})